import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import {
  CompleteRequestWorkflowsResponse,
  CreateWorkflowSuccessResponse,
  DeleteWorkflowResponse,
  GetWorkflowsListResponseSuccess,
  RequestWorkflow,
  RequestWorkflowExternal,
  RequestWorkflowsData,
  RequestWorkflowWithNodes,
  SyncWorkflowFormResponse,
  SyncWorkflowRemoveFileResponse,
  SyncWorkflowUploadFileResponse,
  WorkflowExternal,
  WorkflowFormSubmissionFile,
  WorkflowInput,
  WorkflowNodeExternal,
  WorkflowNodeFormWithPopulatedFieldsExternal,
  WorkflowSchema,
  GetRequestMistakesResponse,
} from '@workflows/types';
import { RegistryCounterparty, RequestDTO } from '@core/types';
import { environment } from '@environment/environment';
import { map, Observable } from 'rxjs';
import { SKIP_GLOBAL_HTTP_ERROR_ALERT } from '@core/interceptors/http-error-context.token';
import { GetEventsOfRequestWorkflowResponse } from '@workflows/types/requestWorkflowEvents/getEventsOfRequestWorkflow';

const { apiUrl } = environment;

@Injectable({
  providedIn: 'root',
})
export class WorkflowApiService {
  private readonly httpClient = inject(HttpClient);

  private createWorkflowHttpContext(): HttpContext {
    return new HttpContext().set(SKIP_GLOBAL_HTTP_ERROR_ALERT, true);
  }

  public getRequestWorkflowsData(
    requestNumber: RequestDTO['requestNumber'],
  ): Observable<RequestWorkflowsData> {
    return this.httpClient
      .get<{
        data: RequestWorkflowsData;
      }>(`${apiUrl}/logistic/request/${requestNumber}/workflows/data`)
      .pipe(map(({ data }) => data));
  }
  // Получить события регламента заявки
  public getEventsOfRequestWorkflow(
    requestNumber: RequestDTO['requestNumber'],
    requestWorkflowId: RequestWorkflowExternal['id'],
  ): Observable<GetEventsOfRequestWorkflowResponse> {
    return this.httpClient.get<GetEventsOfRequestWorkflowResponse>(
      `${apiUrl}/logistic/request/${requestNumber}/workflows/${requestWorkflowId}/events`,
    );
  }

  // Получить события всех регламентов заявки
  public getEventsOfAllRequestWorkflows(
    requestNumber: RequestDTO['requestNumber'],
  ): Observable<GetEventsOfRequestWorkflowResponse> {
    return this.httpClient.get<GetEventsOfRequestWorkflowResponse>(
      `${apiUrl}/logistic/request/${requestNumber}/workflows/events`,
    );
  }

  // Основной метод синхронизации(сабмита) формы регламента
  public syncWorkflow(
    requestNumber: RequestDTO['requestNumber'],
    requestWorkflowId: RequestWorkflowWithNodes['id'],
    nodeId: WorkflowNodeExternal['id'],
    formId: WorkflowNodeFormWithPopulatedFieldsExternal['id'],
    body: object,
    operationId?: string,
  ): Observable<SyncWorkflowFormResponse> {
    return this.httpClient.post<SyncWorkflowFormResponse>(
      `${apiUrl}/logistic/request/${requestNumber}/workflows/${requestWorkflowId}/nodes/${nodeId}/forms/${formId}/actions/sync`,
      operationId ? { ...body, operationId } : body,
      {
        context: this.createWorkflowHttpContext(),
      },
    );
  }

  // Сабмит формы с файлами. Файлы аплоадятся по одному
  // /logistic/request/:requestNumber/workflows/:requestWorkflowId/nodes/:nodeId/forms/:formId/fields/:fieldKey/actions/uploadFile
  public syncWorkflowUploadFile(
    requestNumber: RequestDTO['requestNumber'],
    workflowId: RequestWorkflowWithNodes['id'],
    nodeId: WorkflowNodeExternal['id'],
    formId: WorkflowNodeFormWithPopulatedFieldsExternal['id'],
    fieldId: WorkflowSchema,
    formData: FormData,
    operationId?: string,
  ): Observable<SyncWorkflowUploadFileResponse> {
    // operationId в query: сервер проверяет повтор до чтения файла
    return this.httpClient.post<SyncWorkflowUploadFileResponse>(
      `${apiUrl}/logistic/request/${requestNumber}/workflows/${workflowId}/nodes/${nodeId}/forms/${formId}/fields/${fieldId}/actions/uploadFile`,
      formData,
      {
        context: this.createWorkflowHttpContext(),
        ...(operationId && { params: { operationId } }),
      },
    );
  }

  // Удаление файла из формы регламента
  public syncWorkflowRemoveFile(
    requestNumber: RequestDTO['requestNumber'],
    workflowId: RequestWorkflowWithNodes['id'],
    nodeId: WorkflowNodeExternal['id'],
    formId: WorkflowNodeFormWithPopulatedFieldsExternal['id'],
    fieldId: WorkflowSchema,
    submissionFileId: WorkflowFormSubmissionFile['id'],
    operationId?: string,
  ): Observable<SyncWorkflowRemoveFileResponse> {
    return this.httpClient.delete<SyncWorkflowRemoveFileResponse>(
      `${apiUrl}/logistic/request/${requestNumber}/workflows/${workflowId}/nodes/${nodeId}/forms/${formId}/fields/${fieldId}/actions/removeFile`,
      { body: { submissionFileId, ...(operationId && { operationId }) } },
    );
  }

  // Завершение всех регламентов
  public completeRequestWorkflows(
    requestNumber: RequestDTO['requestNumber'],
    operationId?: string,
  ) {
    return this.httpClient.post<CompleteRequestWorkflowsResponse>(
      `${apiUrl}/logistic/request/${requestNumber}/workflows/complete`,
      operationId ? { operationId } : {},
      {
        context: this.createWorkflowHttpContext(),
      },
    );
  }

  /*------------------------ DEBUG API's ---------------------*/

  public getAllWorkflows(): Observable<GetWorkflowsListResponseSuccess> {
    return this.httpClient.get<GetWorkflowsListResponseSuccess>(
      `${apiUrl}/logistic/workflows`,
    );
  }

  public removeWorkflow(
    workflowId: WorkflowExternal['id'],
  ): Observable<DeleteWorkflowResponse> {
    return this.httpClient.delete<DeleteWorkflowResponse>(
      `${apiUrl}/logistic/workflows/${workflowId}`,
    );
  }

  public getAllRequestWorkflowsOfRequest(
    requestNumber: RequestDTO['requestNumber'],
  ): Observable<RequestWorkflow[]> {
    return this.httpClient.get<RequestWorkflow[]>(
      `${apiUrl}/logistic/request/${requestNumber}/workflows/debug/allRequestWorkflows`,
    );
  }

  public attachSelectedWorkflows(
    requestNumber: RequestDTO['requestNumber'],
    selectedWorkflowIds: string[],
  ): Observable<{ message: string }> {
    return this.httpClient.post<{ message: string }>(
      `${apiUrl}/logistic/request/${requestNumber}/workflows/debug/attachSelectedWorkflows`,
      { selectedWorkflowIds },
    );
  }

  public detachAllWorkflows(
    requestNumber: RequestDTO['requestNumber'],
  ): Observable<{ message: string }> {
    return this.httpClient.post<{ message: string }>(
      `${apiUrl}/logistic/request/${requestNumber}/workflows/debug/detachAllWorkflows`,
      {},
    );
  }

  public clearAllWorkflowsDataForRequest(
    requestNumber: RequestDTO['requestNumber'],
  ): Observable<{ message: string }> {
    return this.httpClient.delete<{ message: string }>(
      `${apiUrl}/logistic/request/${requestNumber}/workflows/debug/clearAllData`,
    );
  }

  public importWorkflow(
    newWorkflow: WorkflowInput,
    publish = false,
  ): Observable<CreateWorkflowSuccessResponse> {
    const params = publish
      ? new HttpParams().set('publish', 'true')
      : undefined;

    return this.httpClient.post<CreateWorkflowSuccessResponse>(
      `${apiUrl}/logistic/workflows`,
      newWorkflow,
      { params, context: this.createWorkflowHttpContext() },
    );
  }

  /*------------------------ END DEBUG API's ---------------------*/

  public setRequestMistakes(
    requestNumber: RequestDTO['requestNumber'],
    requestWorkflowId: RequestWorkflowExternal['id'],
    mistakes: { mistakeTypeId: number; comment: string }[],
  ) {
    // Ошибку показывает диалог ошибок заявки (с причиной), без общего алерта
    return this.httpClient.post(
      `${apiUrl}/logistic/request/${requestNumber}/workflows/${requestWorkflowId}/setRequestMistakes`,
      { mistakes },
      { context: this.createWorkflowHttpContext() },
    );
  }

  /** Текущие ошибки заявки для предзаполнения диалога ошибок */
  public getRequestMistakes(
    requestNumber: RequestDTO['requestNumber'],
    requestWorkflowId: RequestWorkflowExternal['id'],
  ): Observable<GetRequestMistakesResponse> {
    return this.httpClient.get<GetRequestMistakesResponse>(
      `${apiUrl}/logistic/request/${requestNumber}/workflows/${requestWorkflowId}/requestMistakes`,
      { context: this.createWorkflowHttpContext() },
    );
  }

  public getMistakesTypes(counterpartyId: RegistryCounterparty['id']) {
    const params = new HttpParams().set('counterparty', counterpartyId);

    return this.httpClient.get(`${apiUrl}/logistic/mistakeTypelist/`, {
      params,
    });
  }
}
